import { useMemo } from 'react';
import {
  ZONES_BUNDLE,
  REAL_POLY_SOURCES,
  nearestRealL3,
  type ZoneEntry,
} from '@auctionradar/shared';

/**
 * Ultra-light map thumbnail for Feed cards — pure inline SVG, NO Leaflet and
 * NO map tiles, so a 30-card page costs nothing. Renders the lead's L3 zone
 * outline (when it has a real polygon) plus the exact-coordinate dot.
 *
 * Resolution order:
 *   1. `zoneSlug` → that L3 from ZONES_BUNDLE.
 *   2. no slug but coords → nearest real-polygon L3.
 * Degraded states:
 *   - real polygon present  → outline (+ dot if coords).
 *   - coords but no polygon → dot centred on a neutral box.
 *   - neither               → "χωρίς γεω" placeholder.
 */

const ACCENT = '#059669';
const OUTLINE = '#94a3b8';

const hasRealPolygon = (z: ZoneEntry | null): z is ZoneEntry =>
  !!z && !!z.rings && z.rings.length > 0 && REAL_POLY_SOURCES.has(z.polygon_source);

/** Expand a bbox so its aspect ratio matches the SVG box (avoids stretching). */
function fitAspect(
  bbox: [number, number, number, number],
  w: number,
  h: number,
): [number, number, number, number] {
  let [minLng, minLat, maxLng, maxLat] = bbox;
  const cx = (minLng + maxLng) / 2;
  const cy = (minLat + maxLat) / 2;
  let dx = Math.max(maxLng - minLng, 1e-6);
  let dy = Math.max(maxLat - minLat, 1e-6);
  const target = w / h;
  if (dx / dy < target) dx = dy * target;
  else dy = dx / target;
  const padX = dx * 0.12;
  const padY = dy * 0.12;
  return [cx - dx / 2 - padX, cy - dy / 2 - padY, cx + dx / 2 + padX, cy + dy / 2 + padY];
}

export function ZoneSvgThumb({
  zoneSlug,
  lat,
  lng,
  className,
}: {
  zoneSlug: string | null;
  lat: number | null;
  lng: number | null;
  className?: string;
}) {
  const W = 120;
  const H = 80;

  const model = useMemo(() => {
    const hasCoords = lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);

    let zone: ZoneEntry | null = null;
    if (zoneSlug) {
      const idx = ZONES_BUNDLE.by_slug[zoneSlug];
      if (idx != null) zone = ZONES_BUNDLE.zones[idx] ?? null;
    }
    if (!hasRealPolygon(zone) && hasCoords) {
      zone = nearestRealL3(lat as number, lng as number)?.zone ?? zone;
    }

    const poly = hasRealPolygon(zone) ? zone : null;

    // Choose a projection bbox: the polygon's bbox, else a small box around the
    // point, else the (synthetic) zone's bbox/centroid.
    let bbox: [number, number, number, number] | null = null;
    if (poly?.bbox) bbox = poly.bbox;
    else if (hasCoords) {
      const d = 0.006;
      bbox = [(lng as number) - d, (lat as number) - d, (lng as number) + d, (lat as number) + d];
    } else if (zone?.bbox) bbox = zone.bbox;
    else if (zone?.centroid) {
      const [clng, clat] = zone.centroid;
      const d = 0.01;
      bbox = [clng - d, clat - d, clng + d, clat + d];
    }
    if (!bbox) return null;

    const [minLng, minLat, maxLng, maxLat] = fitAspect(bbox, W, H);
    const project = (lo: number, la: number): [number, number] => [
      ((lo - minLng) / (maxLng - minLng)) * W,
      ((maxLat - la) / (maxLat - minLat)) * H,
    ];

    const paths =
      poly?.rings?.map((ring) =>
        ring
          .map(([lo, la], i) => {
            const [x, y] = project(lo, la);
            return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(' ') + ' Z',
      ) ?? [];

    const dot = hasCoords ? project(lng as number, lat as number) : null;

    return { paths, dot };
  }, [zoneSlug, lat, lng]);

  if (!model) {
    return (
      <div
        className={`flex items-center justify-center rounded bg-slate-100 text-[9px] text-slate-400 ${className ?? ''}`}
        style={{ width: W, height: H }}
      >
        χωρίς γεω
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      className={`rounded bg-slate-50 ${className ?? ''}`}
      role="img"
      aria-label="Θέση ακινήτου"
    >
      {model.paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill={ACCENT}
          fillOpacity={0.1}
          stroke={OUTLINE}
          strokeWidth={1}
          strokeLinejoin="round"
        />
      ))}
      {model.dot && (
        <circle cx={model.dot[0]} cy={model.dot[1]} r={3.5} fill={ACCENT} stroke="#fff" strokeWidth={1.5} />
      )}
    </svg>
  );
}
